import { Check, Minus } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { RoleName } from '@/components/RoleName'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Tag } from '@/components/ui/Tag'
import { GRANTS } from '@/features/settings/accessMatrix'
import { useCapabilityCatalogue, useRoles } from '@/features/settings/api/useRoles'
import { capabilityLabelKey, groupCapabilities } from '@/features/settings/capabilityLabels'
import type { CapabilityDescriptor, RoleRecord } from '@/types/domain'

/* «Матрица доступа» — every role side by side, read off the stored records.
 *
 * The role editor above answers "what may THIS role do?"; this answers "who may
 * do X?", which one role at a time cannot. It used to be PRD §3 typed into the
 * client — five fixed columns, hand-written levels, FR/AD codes on the screen —
 * and it drifted from the roles the moment they became editable rows
 * (migration 0021): a created role had no column, and seeded cells disagreed
 * with the stored grants (N-09). It is now built from `GET /admin/roles` and
 * `GET /admin/capabilities`, the same records and vocabulary the role editor
 * reads (the same query keys, so no extra request), and words every cell the
 * way the editor does.
 *
 * It shows STORED settings, deliberately. Where the server narrows a stored
 * level further at read time (the case-money level by the cases scope, the
 * cross-case-finance row no route reads yet — authorization.md), the matrix
 * says what the role editor says rather than re-deriving the server's rules
 * here: a second copy of those rules is how the old table drifted. The one
 * exception is the superuser, read by its FLAG, never its key: the flag passes
 * every check, and its stored lists are usually empty.
 *
 * Read-only by design. Assigning a role or a grant to a person lives on the
 * user record (Settings → Users); changing what a role may do lives in the
 * editor above. Absence rather than disabled controls, so there are no
 * greyed-out buttons here.
 */

export function AccessMatrixPanel() {
  const { t } = useTranslation()
  const roles = useRoles()
  const catalogue = useCapabilityCatalogue()

  // Loading or refused: the roles card above already says which, and a matrix
  // with no columns, or with columns and no rows, would say nothing true.
  if (roles.data === undefined || catalogue.data === undefined) return null

  const columns = roles.data
  const { surfaces, items } = catalogue.data
  const byKey = new Map(items.map((item) => [item.key, item]))
  const areas = groupCapabilities(items.map((item) => item.key)).map(
    ([area, keys]) => [area, keys.flatMap((key) => byKey.get(key) ?? [])] as const,
  )
  const span = columns.length + 1

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.access.title')}</CardTitle>
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('settings.access.subtitle')}
          </p>
        </CardHeader>

        {/* Wide by nature — a column per role. It scrolls inside its own
            container so the page never scrolls sideways; nothing in it is
            editable, so this is not the grid the narrow-screen rule forbids. */}
        <div className="asa-scroll overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">{t('settings.access.tableCaption')}</caption>
            <thead>
              <tr className="border-line border-b">
                <th scope="col" className="asa-eyebrow h-9 min-w-[260px] pr-3 pl-[18px]">
                  {t('settings.access.dataGroup')}
                </th>
                {columns.map((role) => (
                  <th key={role.key} scope="col" className="asa-eyebrow h-9 min-w-[120px] px-3">
                    <RoleName role={role.key} name={role.name} />
                  </th>
                ))}
              </tr>
            </thead>

            {/* ── whose records each role sees ─────────────────────────────── */}
            <tbody>
              <SectionRow span={span} title={t('settings.roles.scopes.title')} />
              {surfaces.map((surface) => (
                <tr key={surface} className="border-line-hair border-b align-top">
                  <RowHeader>{t(`settings.roles.surfaces.${surface}` as never, surface)}</RowHeader>
                  {columns.map((role) => (
                    <td key={role.key} className="px-3 py-2.5">
                      <ScopeCell role={role} surface={surface} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>

            {/* ── what each role may do, area by area ──────────────────────── */}
            <tbody>
              <SectionRow span={span} title={t('settings.roles.capabilities.title')} />
            </tbody>
            {areas.map(([area, members]) => (
              <tbody key={area}>
                <tr>
                  <th
                    scope="rowgroup"
                    colSpan={span}
                    className="asa-eyebrow pt-3 pr-3 pb-1.5 pl-[18px] text-left"
                  >
                    {t(`settings.roles.groups.${area}` as never, area)}
                  </th>
                </tr>
                {members.map((item) => (
                  <tr key={item.key} className="border-line-hair border-b align-top">
                    <RowHeader>
                      <CapabilityName item={item} />
                    </RowHeader>
                    {columns.map((role) => (
                      <td key={role.key} className="px-3 py-2.5">
                        <HeldCell held={role.isSuperuser || role.capabilities.includes(item.key)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>

        <div className="border-line-hair text-text-muted border-t px-[18px] py-2.5 text-[12px] leading-snug font-light">
          {t('settings.access.enforcementNote')}
        </div>
      </Card>

      <div className="grid gap-3.5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.access.grantsTitle')}</CardTitle>
            <p className="text-text-muted text-[12.5px] leading-snug font-light">
              {t('settings.access.grantsSubtitle')}
            </p>
          </CardHeader>
          <CardBody className="flex flex-col pt-2">
            {GRANTS.map((grant) => (
              <div
                key={grant.id}
                className="border-line-hair flex flex-col gap-0.5 border-b py-2.5 last:border-b-0"
              >
                <span className="text-text-heading text-[13px]">{t(grant.labelKey)}</span>
                <span className="text-text-body text-[12px] leading-snug font-light">
                  {t(grant.descriptionKey)}
                </span>
              </div>
            ))}
          </CardBody>
          <div className="border-line-hair text-text-muted border-t px-[18px] py-2.5 text-[12px] leading-snug font-light">
            {t('settings.access.grantsNote')}
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settings.access.boundariesTitle')}</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col gap-3 pt-2">
            {/* The question the matrix reliably raises — "are files redacted
                too?" — answered beside it. It names a capability, not a role,
                so it stays true for a role a founder created. */}
            <div className="flex flex-col gap-1">
              <span className="asa-eyebrow">
                {t('settings.access.boundaries.agentIdentityScope.title')}
              </span>
              <p className="text-text-body text-[12.5px] leading-snug font-light">
                {t('settings.access.boundaries.agentIdentityScope.body')}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
              <Tag tone="brand">{t('settings.access.externalTag')}</Tag>
              <span className="text-text-muted text-[12px] leading-snug font-light">
                {t('settings.access.externalNote')}
              </span>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

/** A section's title across the whole table — «Что видно», «Что можно делать»,
 *  in the role editor's words. A header for the rows below it, not a column. */
function SectionRow({ span, title }: { span: number; title: string }) {
  return (
    <tr className="border-line border-b">
      <th
        scope="rowgroup"
        colSpan={span}
        className="text-text-heading pt-4 pr-3 pb-2 pl-[18px] text-left text-[13px] font-normal"
      >
        {title}
      </th>
    </tr>
  )
}

function RowHeader({ children }: { children: ReactNode }) {
  return (
    <th
      scope="row"
      className="text-text-heading py-2.5 pr-3 pl-[18px] text-left text-[13px] leading-snug font-normal"
    >
      {children}
    </th>
  )
}

/** A capability in the catalogue's words, or the server's description for a
 *  key this client has no words for yet — the role editor's rule. */
function CapabilityName({ item }: { item: CapabilityDescriptor }) {
  const { t } = useTranslation()
  const labelKey = capabilityLabelKey(item.key)
  return <>{labelKey ? t(labelKey) : item.description}</>
}

/** The stored level, worded as the editor's selector words it. A surface
 *  absent from the map is «no access» — never `own` (types/domain.ts). */
function ScopeCell({ role, surface }: { role: RoleRecord; surface: string }) {
  const { t } = useTranslation()
  const level = role.isSuperuser ? 'all' : role.scopes[surface]
  if (level === undefined) {
    return <span className="text-text-muted text-[12.5px] font-light">{t('settings.roles.levels.none')}</span>
  }
  return (
    <span className="text-text-body text-[12.5px]">
      {t(`settings.roles.levels.${level}` as never, level)}
    </span>
  )
}

/** Held or not, by SHAPE and words — a tick or a dash, each with its name for
 *  a screen reader — never by colour alone. */
function HeldCell({ held }: { held: boolean }) {
  const { t } = useTranslation()
  const Icon = held ? Check : Minus
  return (
    <span className={held ? 'text-text-heading' : 'text-text-muted'}>
      <Icon size={14} strokeWidth={2} aria-hidden />
      <span className="sr-only">
        {held ? t('settings.access.held') : t('settings.access.notHeld')}
      </span>
    </span>
  )
}
